import React, { useState, useRef } from 'react';
import Navbar from '../../Navbar';
import Sidebar from '../../Sidebar';
import '../../../styles/TutorialsDashboard.css';

const tutorials = [
  {
    id: 'tutorial1',
    icon: '⚗️',
    title: 'Chemical Reaction Engineering',
    description:
      'A comprehensive study guide covering reactor design fundamentals, mole balances, rate laws, and pressure drop analysis in packed beds — based on Prof. Fogler\'s lectures.',
    tag: 'CRE · Fogler',
    path: '/tutorial1',
    badge: 'Bestseller',
    updated: 'March 2026',
    duration: '12.5 total hours · Beginner Level · Subtitles',
    popupDesc: 'Master the fundamentals of chemical reaction engineering — from mole balances to reactor sizing using Fogler\'s proven methodology.',
    bullets: [
      'Understand mole balances for batch, CSTR, PFR, and PBR reactors',
      'Apply rate laws and stoichiometry to real reaction systems',
      'Analyze pressure drop in packed bed reactors with Ergun equation',
    ],
  },
  {
    id: 'tutorial2',
    icon: '📈',
    title: 'Data Fitting Fundamentals',
    description:
      'Learn how to fit experimental kinetic data to rate law models, evaluate goodness-of-fit metrics, and extract reliable rate parameters using KinetiQ\'s data fitting engine.',
    tag: 'Data Fitting',
    path: '/tutorial2',
    badge: 'Premium',
    updated: 'April 2026',
    duration: '8 total hours · Intermediate Level · Subtitles',
    popupDesc: 'Fit experimental kinetic data to rate law models and extract reliable parameters using KinetiQ\'s built-in data fitting engine.',
    bullets: [
      'Import and pre-process experimental concentration vs. time data',
      'Run nonlinear regression to extract rate constants and orders',
      'Evaluate R², residuals, and confidence intervals for model quality',
    ],
  },
  {
    id: 'tutorial3',
    icon: '🔁',
    title: 'Reactor Design & Sizing',
    description:
      'Step-by-step guide to designing and sizing batch, CSTR, PFR, and PBR reactors. Covers conversion targets, volume calculations, and energy balances.',
    tag: 'Reactor Design',
    path: '/tutorial3',
    badge: 'Bestseller',
    updated: 'February 2026',
    duration: '10 total hours · Beginner Level · Subtitles',
    popupDesc: 'Design and size industrial reactors from scratch — covering all four major reactor types with real-world conversion targets.',
    bullets: [
      'Size CSTR and PFR reactors for a given conversion target',
      'Compare reactor volumes for series and parallel configurations',
      'Incorporate energy balances into isothermal and adiabatic designs',
    ],
  },
  {
    id: 'tutorial4',
    icon: '🌡️',
    title: 'Non-Isothermal Reactor Analysis',
    description:
      'Explore heat effects in reactors, energy balances, and coupled ODE systems for non-isothermal CSTRs and PFRs with temperature profiles.',
    tag: 'Energy Balances',
    path: '/tutorial4',
    badge: 'Premium',
    updated: 'January 2026',
    duration: '9 total hours · Advanced Level · Subtitles',
    popupDesc: 'Solve coupled heat and mass balance ODEs for non-isothermal reactors and generate full temperature-conversion profiles.',
    bullets: [
      'Derive and solve the energy balance for non-isothermal PFRs and CSTRs',
      'Handle exothermic and endothermic reactions with heat exchangers',
      'Use KinetiQ to generate temperature and conversion profiles simultaneously',
    ],
  },
  {
    id: 'tutorial5',
    icon: '🧪',
    title: 'Multiple Reactions & Selectivity',
    description:
      'Understand how to handle parallel and series reactions, maximize selectivity, and minimize unwanted by-products through reactor choice and operating conditions.',
    tag: 'Multiple Reactions',
    path: '/tutorial5',
    badge: 'Bestseller',
    updated: 'March 2026',
    duration: '7.5 total hours · Intermediate Level · Subtitles',
    popupDesc: 'Maximize desired product selectivity in complex reaction networks by choosing the right reactor type and operating conditions.',
    bullets: [
      'Set up mole balances for parallel and series reaction networks',
      'Calculate instantaneous and overall selectivity for competing reactions',
      'Identify the optimal reactor and feed strategy for maximum yield',
    ],
  },
  {
    id: 'tutorial6',
    icon: '📊',
    title: 'Parameter Estimation & Statistics',
    description:
      'Apply nonlinear regression, confidence intervals, and model discrimination techniques to evaluate rival kinetic models and report reliable parameter estimates.',
    tag: 'Statistics',
    path: '/help/tutorials/tutorial6',
    badge: 'Premium',
    updated: 'April 2026',
    duration: '11 total hours · Advanced Level · Subtitles',
    popupDesc: 'Apply rigorous statistical tools to kinetic model fitting — from parameter estimation to model discrimination and uncertainty reporting.',
    bullets: [
      'Perform nonlinear least-squares regression on kinetic datasets',
      'Compute 95% confidence intervals and correlation matrices for parameters',
      'Use F-tests and AIC to discriminate between competing rate law models',
    ],
  },
];

const TutorialsDashboard = () => {
  const [hoveredId, setHoveredId] = useState(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0, side: 'right' });
  const cardRefs = useRef({});

  const handleMouseEnter = (id) => {
    const el = cardRefs.current[id];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const popupWidth = 340;
    const gap = 12;

    let left, side;
    if (rect.right + gap + popupWidth <= viewportWidth) {
      left = rect.right + gap;
      side = 'right';
    } else {
      left = rect.left - popupWidth - gap;
      side = 'left';
    }

    setPopupPos({ top: rect.top + window.scrollY, left, side });
    setHoveredId(id);
  };

  const handleMouseLeave = () => setHoveredId(null);

  const handleCardClick = (path) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const hoveredTut = tutorials.find((t) => t.id === hoveredId);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' }}>

      <Sidebar />

      <div style={{
        marginLeft:    '220px',
        width:         'calc(100% - 220px)',
        display:       'flex',
        flexDirection: 'column',
        height:        '100vh',
        overflow:      'hidden',
      }}>

        <Navbar activePage="app" />

        <div style={{
          flex:      1,
          marginTop: '68px',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
        }}>

      <div className="tdb-page">
        {/* Page header */}
        <div className="tdb-header">
          <div className="tdb-header-tag">Learning Resources</div>
          <h1 className="tdb-header-title">Tutorials</h1>
          <p className="tdb-header-desc">
            Step-by-step guides to help you get the most out of KinetiQ — from
            chemical reaction engineering fundamentals to advanced data fitting
            workflows.
          </p>
        </div>

        {/* Cards grid */}
        <div className="tdb-grid">
          {tutorials.map((tut) => (
            <button
              key={tut.id}
              ref={(el) => (cardRefs.current[tut.id] = el)}
              className={`tdb-card${hoveredId === tut.id ? ' tdb-card--active' : ''}`}
              onClick={() => handleCardClick(tut.path)}
              onMouseEnter={() => handleMouseEnter(tut.id)}
              onMouseLeave={handleMouseLeave}
              aria-label={`Open ${tut.title}`}
            >
              <div className="tdb-card-top">
                <div className="tdb-card-icon">{tut.icon}</div>
                <div className="tdb-card-arrow">→</div>
              </div>
              <div className="tdb-card-body">
                <h3 className="tdb-card-title">{tut.title}</h3>
                <p className="tdb-card-desc">{tut.description}</p>
              </div>
              <div className="tdb-card-footer">
                <span className="tdb-card-tag">{tut.tag}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Hover popup — Udemy-style */}
        {hoveredTut && (
          <div
            className="tdb-popup"
            style={{ top: popupPos.top, left: popupPos.left }}
            onMouseEnter={() => setHoveredId(hoveredTut.id)}
            onMouseLeave={handleMouseLeave}
          >
            <h3 className="tdb-popup-title">{hoveredTut.title}</h3>
            <div className="tdb-popup-badges">
              <span className={`tdb-badge tdb-badge--${hoveredTut.badge === 'Premium' ? 'premium' : 'bestseller'}`}>
                {hoveredTut.badge === 'Premium' ? '✦ Premium' : hoveredTut.badge}
              </span>
              {hoveredTut.badge === 'Premium' && (
                <span className="tdb-badge tdb-badge--bestseller">Bestseller</span>
              )}
            </div>
            <p className="tdb-popup-meta">
              Updated <strong>{hoveredTut.updated}</strong>
            </p>
            <p className="tdb-popup-meta tdb-popup-meta--dim">{hoveredTut.duration}</p>
            <p className="tdb-popup-desc">{hoveredTut.popupDesc}</p>
            <ul className="tdb-popup-bullets">
              {hoveredTut.bullets.map((b, i) => (
                <li key={i}>
                  <span className="tdb-popup-check">✓</span>
                  {b}
                </li>
              ))}
            </ul>
            <button
              className="tdb-popup-cta"
              onClick={() => handleCardClick(hoveredTut.path)}
            >
              Open Tutorial
            </button>
          </div>
        )}
      </div>   {/* end tdb-page */}

        </div>   {/* end scrollable content */}
      </div>     {/* end rightCol */}
    </div>       
  );
};

export default TutorialsDashboard;